import React, { useState, useEffect, useMemo } from "react";
import axios from "utils/api";
import {
  Grid,
  Avatar,
  Chip,
  Card,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArgonBox from "components/ArgonBox";
import {
  Person,
  Phone,
  Home,
  CalendarToday,
  School,
  PersonOutline,
  EmojiEvents,
  AttachMoney,
  Schedule,
  Payment,
  VideoCall,
  Edit,
  ContentCopy,
  SwapHoriz,
  Close,
  Autorenew as AutorenewIcon,
} from "@mui/icons-material";
import ArgonTypography from "components/ArgonTypography";
import Table from "examples/Tables/Table";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SummaryTableData from "Views/Activities/ClassMgt/data/summaryTableData";
import ArgonButton from "components/ArgonButton";

import { toast } from "react-toastify";
import ArgonInput from "components/ArgonInput";
import Dropdown from "components/CustomDropdown/Dropdown";

const CLASS_SESSION_STATUS_OPTIONS = [
  "CREATED",
  "SCHEDULED",
  "ONGOING",
  "CANCELLED",
  "COMPLETED",
  "ON_HOLD",
];

const formatClassSessionStatusLabel = (status) => {
  if (!status) return "N/A";
  return String(status).replace(/_/g, " ");
};

const getClassStatusColor = (status) => {
  switch (status?.toUpperCase()) {
    case "CREATED":
    case "SCHEDULED":
      return "info";
    case "ONGOING":
      return "success";
    case "COMPLETED":
      return "primary";
    case "CANCELLED":
      return "error";
    case "ON_HOLD":
      return "warning";
    default:
      return "default";
  }
};

const getProfitColor = (monthProfitPercent) => {
  if (monthProfitPercent < 20) return "error";
  if (monthProfitPercent <= 25) return "warning";
  return "success";
};

function summary({ classData, refreshClassData }) {
  const toSafeNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const getCycleNumber = (cycle) => {
    const values = [cycle?.cycleNumber, cycle?.monthNumber, cycle?.index, cycle?.cycleIndex];
    const parsed = values.map((value) => Number(value)).find((value) => Number.isFinite(value) && value > 0);
    return parsed || null;
  };

  const getCycleMonthlyFee = (cycle) => toSafeNumber(cycle?.monthlyFee || 0);
  const getCycleTutorPay = (cycle) => toSafeNumber(cycle?.tutorPay || 0);

  const getSelectedCycle = () => {
    const cycles = Array.isArray(classData?.cycles) ? classData.cycles : [];
    if (cycles.length === 0) return null;

    const currentCycleIndex = Number(classData?.currentCycleIndex);
    if (Number.isFinite(currentCycleIndex) && cycles[currentCycleIndex]) {
      return cycles[currentCycleIndex];
    }

    if (classData?.currentCycle) {
      const byName = cycles.find((cycle) => String(cycle?.name || "").trim() === String(classData.currentCycle).trim());
      if (byName) return byName;
    }

    return cycles.find((cycle) => String(cycle?.status || "").toUpperCase() === "ONGOING") || cycles[0] || null;
  };

  const getSelectedCycleSessions = (cycle) =>
    toSafeNumber(cycle?.totalNoOfClasses || cycle?.classes || cycle?.sessionCount || 0);

  const getSelectedCycleRate = (cycle, totalValue) => {
    const sessions = getSelectedCycleSessions(cycle);
    return sessions > 0 ? totalValue / sessions : 0;
  };

  const getTotalCycleValue = (fieldName, fallbackValue = 0) => {
    const cycles = Array.isArray(classData?.cycles) ? classData.cycles : [];
    if (cycles.length === 0) return toSafeNumber(fallbackValue);

    const total = cycles.reduce((sum, cycle) => sum + toSafeNumber(cycle?.[fieldName]), 0);
    return total > 0 ? total : toSafeNumber(fallbackValue);
  };

  const toFixedNumber = (value, digits = 2) => {
    const num = toSafeNumber(value);
    return Number.isFinite(num) ? Number(num.toFixed(digits)) : 0;
  };

  const getTotalClassesForSession = () =>
    toSafeNumber(classData?.totalNoOfClasses || classData?.cycles?.[0]?.totalNoOfClasses || 0);

  const getPerClassFeeCharge = () => {
    const total = getTotalClassesForSession();
    if (!total) return 0;
    return toSafeNumber(classData?.paymentAmount) / total;
  };

  const getRemainingClassesCount = () => {
    const total = getTotalClassesForSession();
    const completed = toSafeNumber(classData?.completedClasses);
    return Math.max(total - completed, 0);
  };

  const getRemainingAmountForReplacement = () => {
    const remainingClasses = getRemainingClassesCount();
    const perClassFee = getPerClassFeeCharge();
    return Math.max(remainingClasses * perClassFee, 0);
  };

  const toIdString = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (typeof value === "object") return value._id || value.id || "";
    return "";
  };

  const inferEducationLevelFromClassName = (classNameRaw) => {
    if (!classNameRaw || typeof classNameRaw !== "string") return null;

    const className = classNameRaw.toLowerCase().trim();
    const classNum = parseInt(className, 10);

    if (
      className.includes("nursery") ||
      className.includes("lkg") ||
      className.includes("ukg") ||
      className.includes("kg") ||
      className.includes("pre-primary") ||
      className.includes("kindergarten")
    ) {
      return "PRIMARY";
    }

    if (
      className.match(/^(6th|7th|8th|class\s*[6-8])$/) ||
      (Number.isFinite(classNum) && classNum >= 6 && classNum <= 8)
    ) {
      return "MIDDLE";
    }

    if (
      className.match(/^(9th|10th|class\s*(9|10))$/) ||
      (Number.isFinite(classNum) && classNum >= 9 && classNum <= 10)
    ) {
      return "SECONDARY";
    }

    if (
      className.match(/^(11th|12th|class\s*(11|12))$/) ||
      (Number.isFinite(classNum) && classNum >= 11 && classNum <= 12)
    ) {
      return "HIGHER";
    }

    return null;
  };

  // Default values if classData is not available
  const parent = classData?.parentId || {};
  // console.log("Parent Data in Summary:", parent);
  const student = classData?.student || {};
  const tutor = classData?.tutor || {};
  const studentsList = classData?.students || [];
  const primaryStudent =
    classData?.student ||
    (Array.isArray(studentsList) && studentsList.length > 0 ? studentsList[0] : {});
  const studentEducationLevel =
    primaryStudent?.educationLevel ||
    inferEducationLevelFromClassName(primaryStudent?.class?.name || "");
  const classSubjectIds = Array.isArray(classData?.subjects)
    ? classData.subjects.map((subject) => toIdString(subject)).filter(Boolean)
    : [];
  const studentSubjectIds = Array.isArray(primaryStudent?.subjects)
    ? primaryStudent.subjects.map((subject) => toIdString(subject)).filter(Boolean)
    : [];
  const pricingSubjectIds = classSubjectIds.length > 0 ? classSubjectIds : studentSubjectIds;
  const { columns, rows } = SummaryTableData(studentsList);
  const classSessionId = classData?._id || "";
  const [coreDialogOpen, setCoreDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [showAllNotesOpen, setShowAllNotesOpen] = useState(false);
  const [showAllCoreOpen, setShowAllCoreOpen] = useState(false);
  const [coreInput, setCoreInput] = useState("");
  const [noteInput, setNoteInput] = useState("");
  const [coreRequirements, setCoreRequirements] = useState([]);
  const [classNotes, setClassNotes] = useState([]);
  const [coreLoading, setCoreLoading] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);
  const [savingCore, setSavingCore] = useState(false);
  const [savingNote, setSavingNote] = useState(false);

  const [openMeetingDialog, setOpenMeetingDialog] = useState(false);
  const [meetingLinkInput, setMeetingLinkInput] = useState("");
  const [meetingLink, setMeetingLink] = useState(classData?.meetingDetails?.meetingLink || "");

  // ===================== Class Renew =====================
  const [openRenewDialog, setOpenRenewDialog] = useState(false);
  const [plansLoading, setPlansLoading] = useState(false);
  const [purchasePlans, setPurchasePlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [renewSaving, setRenewSaving] = useState(false);
  const [renewValues, setRenewValues] = useState({
    totalSessions: "",
    totalCycles: "",
    sessionsPerMonth: "",
    tutorPayoutPerClass: "",
    perClassFees: "",
    perMonthFees: "",
    tutorPayoutTotal: "",
    totalAmount: "",
  });

  // ===================== Class Status Update =====================
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusInput, setStatusInput] = useState(classData?.status || "");
  const [classStatus, setClassStatus] = useState(classData?.status || "");

  useEffect(() => {
    setMeetingLink(classData?.meetingDetails?.meetingLink || "");
  }, [classData?.meetingDetails?.meetingLink]);

  useEffect(() => {
    setClassStatus(classData?.status || "");
  }, [classData?.status]);

  const formatDateTime = (value) => {
    if (!value) return "NA";
    const dateValue = new Date(value);
    if (Number.isNaN(dateValue.getTime())) return "NA";
    return dateValue.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const fetchClassNotes = async () => {
    if (!classSessionId) return;
    try {
      setNotesLoading(true);
      const res = await axios.get(`/classes/session/${classSessionId}/notes`);
      if (res.data?.success) {
        setClassNotes(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setClassNotes([]);
      }
    } catch (error) {
      console.error("Failed to fetch class notes:", error);
      setClassNotes([]);
    } finally {
      setNotesLoading(false);
    }
  };

  const fetchCoreRequirements = async () => {
    if (!classSessionId) return;
    try {
      setCoreLoading(true);
      const res = await axios.get(`/classes/session/${classSessionId}/core-requirements`);
      if (res.data?.success) {
        setCoreRequirements(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setCoreRequirements([]);
      }
    } catch (error) {
      console.error("Failed to fetch core requirements:", error);
      setCoreRequirements([]);
    } finally {
      setCoreLoading(false);
    }
  };

  useEffect(() => {
    if (!classSessionId) return;
    fetchClassNotes();
    fetchCoreRequirements();
  }, [classSessionId]);

  const handleOpenCoreDialog = () => {
    setCoreInput("");
    setCoreDialogOpen(true);
  };

  const handleCloseCoreDialog = () => {
    if (savingCore) return;
    setCoreDialogOpen(false);
  };

  const handleOpenNotesDialog = () => {
    setNoteInput("");
    setNotesDialogOpen(true);
  };

  const handleCloseNotesDialog = () => {
    if (savingNote) return;
    setNotesDialogOpen(false);
  };

  const handleOpenShowAllNotes = () => {
    setShowAllNotesOpen(true);
  };

  const handleCloseShowAllNotes = () => {
    setShowAllNotesOpen(false);
  };

  const handleOpenShowAllCore = () => {
    setShowAllCoreOpen(true);
  };

  const handleCloseShowAllCore = () => {
    setShowAllCoreOpen(false);
  };

  const handleSaveCoreRequirement = async (e) => {
    if (e) e.preventDefault();
    if (!classSessionId) {
      toast.error("Class ID is missing");
      return;
    }

    if (!coreInput.trim()) {
      toast.error("Please enter a core requirement");
      return;
    }

    try {
      setSavingCore(true);
      const res = await axios.post(`/classes/session/${classSessionId}/core-requirements`, {
        requirement: coreInput.trim(),
      });
      if (res.data?.success) {
        toast.success("Core requirement added successfully");
        setCoreDialogOpen(false);
        setCoreInput("");
        await fetchCoreRequirements();
      } else {
        throw new Error(res.data?.message || "Failed to add core requirement");
      }
    } catch (error) {
      console.error("Failed to add core requirement:", error);
      toast.error(
        error.response?.data?.message || error.message || "Failed to add core requirement"
      );
    } finally {
      setSavingCore(false);
    }
  };

  const handleSaveClassNote = async (e) => {
    if (e) e.preventDefault();
    if (!classSessionId) {
      toast.error("Class ID is missing");
      return;
    }

    if (!noteInput.trim()) {
      toast.error("Please enter a note");
      return;
    }

    try {
      setSavingNote(true);
      const res = await axios.post(`/classes/session/${classSessionId}/notes`, {
        note: noteInput.trim(),
      });
      if (res.data?.success) {
        toast.success("Note added successfully");
        setNotesDialogOpen(false);
        setNoteInput("");
        await fetchClassNotes();
      } else {
        throw new Error(res.data?.message || "Failed to add note");
      }
    } catch (error) {
      console.error("Failed to add class note:", error);
      toast.error(error.response?.data?.message || error.message || "Failed to add note");
    } finally {
      setSavingNote(false);
    }
  };

  const fetchPurchasePlans = async () => {
    try {
      setPlansLoading(true);
      const res = await axios.get("/planwithstudentreview");
      const plans = Array.isArray(res?.data?.data?.proposalPlan) ? res.data.data.proposalPlan : [];
      setPurchasePlans(plans);
    } catch (error) {
      console.error("Failed to fetch purchase plans:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Failed to load purchase plans"
      );
      setPurchasePlans([]);
    } finally {
      setPlansLoading(false);
    }
  };

  const handleOpenRenewDialog = async () => {
    setSelectedPlanId("");
    setOpenRenewDialog(true);
    await fetchPurchasePlans();
  };

  const handleCloseRenewDialog = () => {
    if (renewSaving) return;
    setOpenRenewDialog(false);
  };

  const handleOpenMeetingDialog = () => {
    setMeetingLinkInput(meetingLink || classData?.meetingDetails?.meetingLink || "");
    setOpenMeetingDialog(true);
  };

  const handleCloseMeetingDialog = () => {
    setOpenMeetingDialog(false);
    setMeetingLinkInput("");
  };

  const handleCopyMeetingLink = () => {
    const link = meetingLink;
    if (link) {
      navigator.clipboard.writeText(link);
      toast.success("Meeting link copied!");
    } else {
      toast.error("No meeting link available");
    }
  };

  const handleUpdateMeetingLink = async () => {
    // Add your API call here to update meeting link
    try {
      if (!classData?._id) throw new Error("Missing class id");

      const payload = { meetingDetails: { meetingLink: meetingLinkInput } };
      const res = await axios.put(`/classes/session/${classData._id}`, payload);
      if (res?.data?.success) {
        // Persisted on backend — update local UI
        setMeetingLink(meetingLinkInput);
        toast.success(res.data?.message || "Meeting link updated successfully!");
        handleCloseMeetingDialog();
      } else {
        throw new Error(res?.data?.message || "Failed to update meeting link");
      }
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || "Failed to update meeting link");
      console.error("Meeting link update error:", error);
    }
  };

  const handleOpenStatusDialog = () => {
    setStatusInput(classStatus || classData?.status || "");
    setOpenStatusDialog(true);
  };

  const handleCloseStatusDialog = () => {
    if (statusSaving) return;
    setOpenStatusDialog(false);
  };

  const handleUpdateClassStatus = async () => {
    try {
      if (!classData?._id) throw new Error("Missing class session id");
      if (!statusInput) {
        toast.error("Please select a status");
        return;
      }

      setStatusSaving(true);
      const res = await axios.put(`/classes/session/${classData._id}`, {
        status: statusInput,
      });

      if (res?.data?.success) {
        setClassStatus(statusInput);
        toast.success("Class status updated successfully");
        setOpenStatusDialog(false);
        if (typeof refreshClassData === "function") {
          await refreshClassData();
        }
      } else {
        throw new Error(res?.data?.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Status update error:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Failed to update status"
      );
    } finally {
      setStatusSaving(false);
    }
  };

  const getClassesPerWeek = () =>
    toSafeNumber(classData?.numberOfClassesInAWeek || classData?.classesPerWeek || 0);

  const getSessionDurationHours = () => {
    const durationValue = classData?.duration || 60;
    return durationValue > 24 ? durationValue / 60 : durationValue;
  };

  const resolveTutorHourlyRate = (tutorItem) => {
    const directRate = toSafeNumber(tutorItem?.hourlyRate);
    if (directRate > 0) {
      return directRate;
    }

    const minuteRate = toSafeNumber(tutorItem?.hourlyRateInMinutes);
    if (minuteRate > 0) {
      return minuteRate * 60;
    }

    const tutorPricing = Array.isArray(tutorItem?.levelBasedPricing)
      ? tutorItem.levelBasedPricing
      : [];
    const firstPositivePricing = tutorPricing.find(
      (pricing) => toSafeNumber(pricing?.hourlyRate) > 0
    );

    return toSafeNumber(firstPositivePricing?.hourlyRate || 0);
  };

  const getTutorPricingForRenewal = () => {
    const tutorPricing = Array.isArray(tutor?.levelBasedPricing) ? tutor.levelBasedPricing : [];
    let matchedPricing = null;

    if (studentEducationLevel && pricingSubjectIds.length > 0) {
      matchedPricing = tutorPricing.find((pricing) => {
        if (pricing?.level !== studentEducationLevel) return false;

        const pricingSubjects = Array.isArray(pricing?.subjects)
          ? pricing.subjects.map((subject) => toIdString(subject)).filter(Boolean)
          : [];

        if (pricingSubjects.length === 0) return false;
        return pricingSubjectIds.some((subjectId) => pricingSubjects.includes(subjectId));
      });
    }

    if (!matchedPricing && studentEducationLevel) {
      matchedPricing = tutorPricing.find((pricing) => pricing?.level === studentEducationLevel);
    }

    const hourlyRate = toSafeNumber(
      matchedPricing?.hourlyRate || tutor?.hourlyRate || classData?.tutorSalary || 0
    );
    const marginPercent = toSafeNumber(
      matchedPricing?.edushalaMargin ?? tutor?.edushalaMargin ?? classData?.grossMargin ?? 0
    );
    const sessionHours = getSessionDurationHours();
    const tutorSalaryPerClass = hourlyRate > 0 ? hourlyRate * sessionHours : 0;
    const feeChargePerClass = tutorSalaryPerClass * (1 + marginPercent / 100);

    return {
      hourlyRate,
      marginPercent,
      tutorSalaryPerClass,
      feeChargePerClass,
    };
  };

  const selectedPlan = purchasePlans.find((plan) => String(plan?._id) === String(selectedPlanId));
  const selectedPlanMonths = toSafeNumber(selectedPlan?.months || 0);
  const sessionsPerWeek = getClassesPerWeek();
  const sessionsPerMonth = sessionsPerWeek > 0 ? sessionsPerWeek * 4 : 0;
  const totalSessions = selectedPlanMonths > 0 ? sessionsPerMonth * selectedPlanMonths : 0;
  const totalCycles = selectedPlanMonths;

  const renewalPricing = getTutorPricingForRenewal();
  const perClassFees = renewalPricing.feeChargePerClass;
  const perMonthFees = sessionsPerMonth * perClassFees;
  const discountPercent = toSafeNumber(selectedPlan?.discountPercentage || 0);
  const totalAmountBeforeDiscount = perMonthFees * selectedPlanMonths;
  const totalAmountAfterDiscount = totalAmountBeforeDiscount * (1 - discountPercent / 100);
  const perMonthFinalFee =
    selectedPlanMonths > 0 ? totalAmountAfterDiscount / selectedPlanMonths : 0;
  const tutorTotalPayout = renewalPricing.tutorSalaryPerClass * totalSessions;

  useEffect(() => {
    if (!selectedPlanId) {
      setRenewValues({
        totalSessions: "",
        totalCycles: "",
        sessionsPerMonth: "",
        tutorPayoutPerClass: "",
        perClassFees: "",
        perMonthFees: "",
        tutorPayoutTotal: "",
        totalAmount: "",
      });
      return;
    }

    setRenewValues({
      totalSessions: totalSessions ? String(Math.round(totalSessions)) : "0",
      totalCycles: totalCycles ? String(Math.round(totalCycles)) : "0",
      sessionsPerMonth: sessionsPerMonth ? String(Math.round(sessionsPerMonth)) : "0",
      tutorPayoutPerClass: renewalPricing.tutorSalaryPerClass
        ? String(Math.round(renewalPricing.tutorSalaryPerClass))
        : "0",
      perClassFees: perClassFees ? String(Math.round(perClassFees)) : "0",
      perMonthFees: perMonthFinalFee ? String(Math.round(perMonthFinalFee)) : "0",
      tutorPayoutTotal: tutorTotalPayout ? String(Math.round(tutorTotalPayout)) : "0",
      totalAmount: totalAmountAfterDiscount ? String(Math.round(totalAmountAfterDiscount)) : "0",
    });
  }, [
    selectedPlanId,
    totalSessions,
    totalCycles,
    sessionsPerMonth,
    perClassFees,
    perMonthFinalFee,
    tutorTotalPayout,
    totalAmountAfterDiscount,
  ]);

  const handleRenewValueChange = (field) => (event) => {
    const value = event.target.value;
    setRenewValues((prev) => {
      const next = {
        ...prev,
        [field]: value,
      };

      if (field === "tutorPayoutPerClass" || field === "totalSessions") {
        const perClass = toSafeNumber(next.tutorPayoutPerClass);
        const totalSessionsCount = toSafeNumber(next.totalSessions);
        const totalPayout =
          perClass > 0 && totalSessionsCount > 0 ? perClass * totalSessionsCount : 0;
        next.tutorPayoutTotal = totalPayout ? String(Math.round(totalPayout)) : "0";
      }

      if (field === "perClassFees" || field === "sessionsPerMonth") {
        const perClassFee = toSafeNumber(next.perClassFees);
        const sessionsMonth = toSafeNumber(next.sessionsPerMonth);
        const perMonth = perClassFee > 0 && sessionsMonth > 0 ? perClassFee * sessionsMonth : 0;
        next.perMonthFees = perMonth ? String(Math.round(perMonth)) : "0";
      }

      if (
        field === "perMonthFees" ||
        field === "totalCycles" ||
        field === "perClassFees" ||
        field === "sessionsPerMonth"
      ) {
        const perMonthFee = toSafeNumber(next.perMonthFees);
        const cycles = toSafeNumber(next.totalCycles);
        const totalAmount = perMonthFee > 0 && cycles > 0 ? perMonthFee * cycles : 0;
        next.totalAmount = totalAmount ? String(Math.round(totalAmount)) : "0";
      }

      return next;
    });
  };

  const handleRenewClass = async () => {
    try {
      if (!classData?._id) throw new Error("Missing class session id");
      if (!selectedPlan) {
        toast.error("Please select a plan");
        return;
      }

      if (selectedPlanMonths <= 0) {
        toast.error("Invalid plan months");
        return;
      }

      if (sessionsPerWeek <= 0) {
        toast.error("Number of classes per week is not set for this class");
        return;
      }

      if (renewalPricing.tutorSalaryPerClass <= 0 || perClassFees <= 0) {
        toast.error("Tutor pricing is not available for this class");
        return;
      }

      const resolvedTotalSessions = toSafeNumber(
        renewValues.totalSessions !== "" ? renewValues.totalSessions : totalSessions
      );
      const resolvedTotalCycles = toSafeNumber(
        renewValues.totalCycles !== "" ? renewValues.totalCycles : totalCycles
      );
      const resolvedSessionsPerMonth = toSafeNumber(
        renewValues.sessionsPerMonth !== "" ? renewValues.sessionsPerMonth : sessionsPerMonth
      );
      const resolvedTutorPayoutPerClass = toSafeNumber(
        renewValues.tutorPayoutPerClass !== ""
          ? renewValues.tutorPayoutPerClass
          : renewalPricing.tutorSalaryPerClass
      );
      const resolvedPerClassFees = toSafeNumber(
        renewValues.perClassFees !== "" ? renewValues.perClassFees : perClassFees
      );
      const resolvedPerMonthFees = toSafeNumber(
        renewValues.perMonthFees !== "" ? renewValues.perMonthFees : perMonthFinalFee
      );
      const resolvedTotalAmount = toSafeNumber(
        renewValues.totalAmount !== "" ? renewValues.totalAmount : totalAmountAfterDiscount
      );

      if (resolvedTotalSessions <= 0) {
        toast.error("Total sessions must be greater than 0");
        return;
      }

      if (resolvedTutorPayoutPerClass <= 0) {
        toast.error("Tutor payout per class must be greater than 0");
        return;
      }

      setRenewSaving(true);
      const payload = {
        totalNoOfClasses: resolvedTotalSessions,
        noOfMonths: selectedPlanMonths,
        numberOfClassesInAWeek: sessionsPerWeek,
        paymentAmount: Math.round(resolvedTotalAmount),
        feeCharge: toFixedNumber(resolvedPerClassFees, 2),
        tutorSalary: toFixedNumber(resolvedTutorPayoutPerClass, 2),
        grossMargin: toFixedNumber(renewalPricing.marginPercent, 2),
        perSessionFee: toFixedNumber(resolvedPerClassFees, 2),
        monthlyFee: toFixedNumber(resolvedPerMonthFees, 2),
        appendCycles: true,
      };

      const res = await axios.put(`/classes/session/${classData._id}`, payload);
      if (res?.data?.success) {
        toast.success(res.data?.message || "Class renewed successfully");
        setOpenRenewDialog(false);
        if (typeof refreshClassData === "function") {
          await refreshClassData();
        }
      } else {
        throw new Error(res?.data?.message || "Failed to renew class");
      }
    } catch (error) {
      console.error("Class renew error:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Failed to renew class"
      );
    } finally {
      setRenewSaving(false);
    }
  };

  // ===================== Tutor Replacement =====================
  const [openTutorReplacementDialog, setOpenTutorReplacementDialog] = useState(false);
  const [replacementSaving, setReplacementSaving] = useState(false);
  const [approvedTutorsLoading, setApprovedTutorsLoading] = useState(false);
  const [approvedTutors, setApprovedTutors] = useState([]);
  const [selectedReplacementTutorId, setSelectedReplacementTutorId] = useState("");
  const [replacementHourlyRate, setReplacementHourlyRate] = useState("");
  const [replacementNoOfSession, setReplacementNoOfSession] = useState("");
  const [replacementMargin, setReplacementMargin] = useState("");
  const [replacementParent, setReplacementParent] = useState(null);

  const normalizeLatLng = (value1, value2) => {
    const val1 = Number(value1);
    const val2 = Number(value2);
    if (!Number.isFinite(val1) || !Number.isFinite(val2)) return null;

    const val1Lat = Math.abs(val1) <= 90;
    const val1Lng = Math.abs(val1) <= 180;
    const val2Lat = Math.abs(val2) <= 90;
    const val2Lng = Math.abs(val2) <= 180;

    if (val1Lat && val2Lng) return { lat: val1, lng: val2 };
    if (val2Lat && val1Lng) return { lat: val2, lng: val1 };
    return null;
  };

  const getLocationCoords = (location) => {
    if (!location) return null;
    if (Array.isArray(location.coordinates) && location.coordinates.length === 2) {
      return normalizeLatLng(location.coordinates[0], location.coordinates[1]);
    }
    if (Number.isFinite(location.lat) && Number.isFinite(location.lng)) {
      return { lat: Number(location.lat), lng: Number(location.lng) };
    }
    return null;
  };

  const getEntityCoords = (entity) => {
    if (!entity) return null;
    const directCoords = getLocationCoords(entity.location);
    if (directCoords) return directCoords;

    const preferences = Array.isArray(entity.locationPreference) ? entity.locationPreference : [];
    for (const pref of preferences) {
      if (Number.isFinite(pref?.lat) && Number.isFinite(pref?.lng)) {
        return { lat: Number(pref.lat), lng: Number(pref.lng) };
      }
    }

    return null;
  };

  const toRadians = (value) => (Number(value) * Math.PI) / 180;

  const getDistanceKm = (coordsA, coordsB) => {
    if (!coordsA || !coordsB) return null;
    const earthRadius = 6371;
    const dLat = toRadians(coordsB.lat - coordsA.lat);
    const dLng = toRadians(coordsB.lng - coordsA.lng);
    const lat1 = toRadians(coordsA.lat);
    const lat2 = toRadians(coordsB.lat);

    const sinLat = Math.sin(dLat / 2);
    const sinLng = Math.sin(dLng / 2);
    const a = sinLat * sinLat + Math.cos(lat1) * Math.cos(lat2) * sinLng * sinLng;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return earthRadius * c;
  };

  const formatTutorLabel = (tutor, distanceKm) => {
    const name = tutor?.fullName || "N/A";
    const distanceLabel = Number.isFinite(distanceKm) ? `${distanceKm.toFixed(1)} km` : "N/A";
    return `${name} • ${distanceLabel}`;
  };

  const sortedApprovedTutors = useMemo(() => {
    const parentCoords = getEntityCoords(replacementParent || parent);
    const enrichedTutors = approvedTutors.map((item) => {
      const tutorCoords = getEntityCoords(item);
      const distanceKm = parentCoords ? getDistanceKm(parentCoords, tutorCoords) : null;
      return {
        ...item,
        distanceKm,
      };
    });

    if (!parentCoords) {
      return enrichedTutors;
    }

    return [...enrichedTutors].sort((a, b) => {
      const distA = Number.isFinite(a.distanceKm) ? a.distanceKm : Number.POSITIVE_INFINITY;
      const distB = Number.isFinite(b.distanceKm) ? b.distanceKm : Number.POSITIVE_INFINITY;
      return distA - distB;
    });
  }, [approvedTutors, parent, replacementParent]);

  useEffect(() => {
    const parentId =
      parent?._id || parent?.id || classData?.parentId?._id || classData?.parentId || "";

    if (!openTutorReplacementDialog || !parentId) {
      return;
    }

    const fetchParentDetails = async () => {
      try {
        const res = await axios.get("/parent", { params: { id: parentId } });
        const parentDoc = res?.data?.data || null;
        setReplacementParent(parentDoc);
      } catch (error) {
        console.error("Failed to fetch parent details:", error);
      }
    };

    fetchParentDetails();
  }, [openTutorReplacementDialog, parent, classData?.parentId]);

  const fetchApprovedTutors = async () => {
    try {
      setApprovedTutorsLoading(true);

      const res = await axios.get("/tutor", {
        params: {
          approvalStatus: "APPROVED",
          page: 1,
          limit: 100,
          sort: "createdAt",
          order: "desc",
        },
      });

      const tutorsData = Array.isArray(res?.data?.data?.tutors)
        ? res.data.data.tutors
        : Array.isArray(res?.data?.tutors)
        ? res.data.tutors
        : [];

      const currentTutorId = String(tutor?._id || tutor?.id || classData?.tutorId || "");
      const normalizedTutors = tutorsData
        .map((item) => ({
          id: item?.id || item?._id,
          fullName: item?.fullName || item?.name || "N/A",
          hourlyRate: toSafeNumber(item?.hourlyRate),
          hourlyRateInMinutes: toSafeNumber(item?.hourlyRateInMinutes),
          edushalaMargin: toSafeNumber(item?.edushalaMargin),
          levelBasedPricing: Array.isArray(item?.levelBasedPricing) ? item.levelBasedPricing : [],
          location: item?.location,
          locationPreference: Array.isArray(item?.locationPreference)
            ? item.locationPreference
            : [],
          isCurrentTutor: String(item?.id || item?._id || "") === currentTutorId,
        }))
        .filter((item) => item.id);

      setApprovedTutors(normalizedTutors);
    } catch (error) {
      console.error("Failed to fetch approved tutors:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Failed to load approved tutors"
      );
      setApprovedTutors([]);
    } finally {
      setApprovedTutorsLoading(false);
    }
  };

  const handleOpenTutorReplacementDialog = async () => {
    const remainingClasses = getRemainingClassesCount();

    setSelectedReplacementTutorId("");
    setReplacementHourlyRate(
      toSafeNumber(classData?.tutorSalary || tutor?.hourlyRate || 0).toString()
    );
    setReplacementNoOfSession(remainingClasses.toString());
    setReplacementMargin(toSafeNumber(classData?.grossMargin).toString());
    setOpenTutorReplacementDialog(true);

    await fetchApprovedTutors();
  };

  const handleCloseTutorReplacementDialog = () => {
    if (replacementSaving) return;
    setOpenTutorReplacementDialog(false);
  };

  const handleTutorSelectionChange = (selectedTutorId) => {
    setSelectedReplacementTutorId(selectedTutorId);
    const selectedTutor = approvedTutors.find(
      (item) => String(item.id) === String(selectedTutorId)
    );

    if (!selectedTutor) return;

    const resolvedRate = resolveTutorHourlyRate(selectedTutor);
    const tutorPricing = Array.isArray(selectedTutor.levelBasedPricing)
      ? selectedTutor.levelBasedPricing
      : [];
    const firstPositivePricing = tutorPricing.find(
      (pricing) => toSafeNumber(pricing?.hourlyRate) > 0
    );
    const resolvedMargin = toSafeNumber(
      firstPositivePricing?.edushalaMargin ??
        selectedTutor.edushalaMargin ??
        classData?.grossMargin ??
        0
    );

    setReplacementHourlyRate(resolvedRate.toString());
    setReplacementMargin(resolvedMargin.toString());

    if (resolvedRate <= 0) {
      toast.warn(
        "The selected tutor’s hourly rate is 0. Please verify and update the rate before submitting."
      );
    }
  };

  const handleSubmitTutorReplacement = async () => {
    try {
      if (!classData?._id) throw new Error("Missing class session id");
      if (!selectedReplacementTutorId) {
        toast.error("Please select new tutor");
        return;
      }

      const currentTutorId = String(tutor?._id || tutor?.id || classData?.tutorId || "");
      if (String(selectedReplacementTutorId) === currentTutorId) {
        toast.error("Please select a different tutor");
        return;
      }

      const hourlyRateNum = toSafeNumber(replacementHourlyRate);
      const noOfSessionNum = Math.floor(toSafeNumber(replacementNoOfSession));
      const marginNum = toSafeNumber(replacementMargin);

      if (hourlyRateNum <= 0) {
        toast.error("Hourly rate should be greater than 0");
        return;
      }
      if (noOfSessionNum <= 0) {
        toast.error("No of session should be greater than 0");
        return;
      }
      if (marginNum < 0) {
        toast.error("Margin cannot be negative");
        return;
      }

      const remainingClasses = getRemainingClassesCount();
      if (noOfSessionNum > remainingClasses) {
        toast.error(`No of session cannot exceed remaining sessions (${remainingClasses})`);
        return;
      }

      const remainingAmount = getRemainingAmountForReplacement();
      const newTutorTotalPayout = hourlyRateNum * noOfSessionNum;
      if (newTutorTotalPayout > remainingAmount) {
        toast.error(
          `New tutor payout (₹${Math.round(
            newTutorTotalPayout
          )}) cannot exceed parent remaining amount (₹${Math.round(remainingAmount)})`
        );
        return;
      }

      const completedClasses = toSafeNumber(classData?.completedClasses);
      const updatedTotalNoOfClasses = completedClasses + noOfSessionNum;
      const recalculatedFeeCharge = hourlyRateNum * (1 + marginNum / 100);

      setReplacementSaving(true);
      const res = await axios.put(`/classes/session/${classData._id}`, {
        tutorId: selectedReplacementTutorId,
        tutorSalary: hourlyRateNum,
        grossMargin: marginNum,
        feeCharge: recalculatedFeeCharge,
        totalNoOfClasses: updatedTotalNoOfClasses,
      });

      if (res?.data?.success) {
        toast.success("Tutor replaced successfully");
        setOpenTutorReplacementDialog(false);
        if (typeof refreshClassData === "function") {
          await refreshClassData();
        }
      } else {
        throw new Error(res?.data?.message || "Failed to replace tutor");
      }
    } catch (error) {
      console.error("Tutor replacement error:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Failed to replace tutor"
      );
    } finally {
      setReplacementSaving(false);
    }
  };

  // ===================== Slot Update (Finalised Active Slot) =====================
  const [openSlotDialog, setOpenSlotDialog] = useState(false);
  const [slotDialogLoading, setSlotDialogLoading] = useState(false);
  const [slotDialogSaving, setSlotDialogSaving] = useState(false);
  const [allSlots, setAllSlots] = useState([]);
  const [busySlotIds, setBusySlotIds] = useState([]);
  const [preferredSlotIds, setPreferredSlotIds] = useState([]);
  const [selectedSlotId, setSelectedSlotId] = useState("");

  const currentFinalSlot =
    classData?.finalisedActiveSlot && typeof classData.finalisedActiveSlot === "object"
      ? classData.finalisedActiveSlot
      : null;
  const currentFinalSlotId =
    currentFinalSlot?._id ||
    currentFinalSlot?.id ||
    (typeof classData?.finalisedActiveSlot === "string" ? classData.finalisedActiveSlot : "");
  const currentFinalSlotLabel =
    currentFinalSlot?.startTime && currentFinalSlot?.endTime
      ? `${currentFinalSlot.startTime} - ${currentFinalSlot.endTime}`
      : "N/A";

  const getSlotId = (slot) => {
    if (!slot) return "";
    if (typeof slot === "string") return slot;
    return slot._id || slot.id || "";
  };

  const getSlotLabel = (slot) => {
    if (!slot) return "N/A";
    const start = slot.startTime || slot.start || "";
    const end = slot.endTime || slot.end || "";
    if (start && end) return `${start} - ${end}`;
    return start || end || "N/A";
  };

  const getPreferredSlotIds = (preferredTeachingSlots) => {
    if (!Array.isArray(preferredTeachingSlots)) return [];
    return preferredTeachingSlots
      .map((s) => getSlotId(s))
      .filter(Boolean)
      .map((x) => String(x));
  };

  const fetchTutorSlotPopupData = async () => {
    const tutorIdToUse =
      tutor?._id || tutor?.id || classData?.tutorId?._id || classData?.tutorId || "";

    if (!tutorIdToUse) {
      setAllSlots([]);
      setBusySlotIds([]);
      setPreferredSlotIds([]);
      toast.error("Tutor not found for this class session");
      return;
    }

    const localTutorObj =
      tutor || (typeof classData?.tutorId === "object" ? classData?.tutorId : null) || null;
    const localPreferredIds = getPreferredSlotIds(localTutorObj?.preferredTeachingSlots);
    setPreferredSlotIds(localPreferredIds);

    setSlotDialogLoading(true);
    try {
      const slotsReq = axios.get("/slots", {
        params: {
          page: 1,
          limit: 100,
          sort: "startTime",
          order: "asc",
        },
      });

      const activeSessionsReq = axios.get(`/classes/get/active/classesFor/tutor/${tutorIdToUse}`, {
        params: { status: "ACTIVE" },
      });

      const [slotsRes, activeSessionsRes] = await Promise.all([slotsReq, activeSessionsReq]);

      // Normalize slots response
      let slotsData = [];
      if (slotsRes?.data?.data?.slots) {
        slotsData = slotsRes.data.data.slots;
      } else if (Array.isArray(slotsRes?.data?.data)) {
        slotsData = slotsRes.data.data;
      } else if (slotsRes?.data?.data) {
        slotsData = slotsRes.data.data;
      } else if (Array.isArray(slotsRes?.data?.slots)) {
        slotsData = slotsRes.data.slots;
      } else if (Array.isArray(slotsRes?.data)) {
        slotsData = slotsRes.data;
      }

      const normalizedSlots = Array.isArray(slotsData) ? slotsData : [];

      // If backend has more than 100 slots, auto-fetch remaining pages and merge
      const totalPages =
        slotsRes?.data?.data?.pagination?.totalPages ||
        slotsRes?.data?.data?.pagination?.total_page ||
        1;
      let mergedSlots = [...normalizedSlots];

      if (Number(totalPages) > 1) {
        const pagePromises = [];
        for (let page = 2; page <= Number(totalPages); page += 1) {
          pagePromises.push(
            axios.get("/slots", {
              params: {
                page,
                limit: 100,
                sort: "startTime",
                order: "asc",
              },
            })
          );
        }

        const pageResponses = await Promise.all(pagePromises);
        pageResponses.forEach((res) => {
          const pageSlots = res?.data?.data?.slots;
          if (Array.isArray(pageSlots) && pageSlots.length > 0) {
            mergedSlots.push(...pageSlots);
          }
        });
      }

      // De-duplicate by id (safety) + sort by startTime
      const byId = new Map();
      mergedSlots.forEach((s) => {
        const id = getSlotId(s);
        if (id) byId.set(String(id), s);
      });

      const sortedSlots = Array.from(byId.values()).sort((a, b) => {
        const aStart = (a?.startTime || a?.start || "").toString();
        const bStart = (b?.startTime || b?.start || "").toString();
        return aStart.localeCompare(bStart);
      });

      setAllSlots(sortedSlots);

      // Preferred teaching slots (highlight only; not used for busy logic)
      if (localPreferredIds.length === 0) {
        try {
          const tutorPrefRes = await axios.get("/tutor", {
            params: { id: tutorIdToUse },
          });

          const tutorDoc =
            tutorPrefRes?.data?.data?.tutor ||
            tutorPrefRes?.data?.data ||
            tutorPrefRes?.data?.tutor ||
            null;
          const preferredTeachingSlots =
            tutorDoc?.preferredTeachingSlots ||
            tutorPrefRes?.data?.data?.preferredTeachingSlots ||
            [];
          const preferredIds = getPreferredSlotIds(preferredTeachingSlots);
          setPreferredSlotIds([...new Set(preferredIds)]);
        } catch (prefErr) {
          // If this fails, we still show all slots; just no preferred highlight
          console.warn("Failed to fetch preferredTeachingSlots:", prefErr);
        }
      }

      // Normalize active sessions response
      const sessionsArr = Array.isArray(activeSessionsRes?.data?.classes)
        ? activeSessionsRes.data.classes
        : Array.isArray(activeSessionsRes?.data?.data?.classes)
        ? activeSessionsRes.data.data.classes
        : Array.isArray(activeSessionsRes?.data?.data)
        ? activeSessionsRes.data.data
        : [];
      const currentSessionIdStr = classData?._id ? String(classData._id) : "";
      const busyIds = sessionsArr
        .filter((s) => String(s?._id || s?.id || "") !== currentSessionIdStr)
        .map((s) => {
          const slotRef = s?.finalisedActiveSlot;
          if (!slotRef) return null;
          if (typeof slotRef === "string") return slotRef;
          return slotRef._id || slotRef.id || null;
        })
        .filter(Boolean)
        .map((x) => String(x));

      setBusySlotIds([...new Set(busyIds)]);
    } catch (error) {
      console.error("Error fetching tutor slot popup data:", error);
      setAllSlots([]);
      setBusySlotIds([]);
      setPreferredSlotIds(localPreferredIds);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Failed to load tutor slots"
      );
    } finally {
      setSlotDialogLoading(false);
    }
  };

  const preferredSlotIdSet = new Set(
    (Array.isArray(preferredSlotIds) ? preferredSlotIds : []).map((x) => String(x))
  );

  const handleOpenSlotDialog = async () => {
    setSelectedSlotId(currentFinalSlotId ? String(currentFinalSlotId) : "");
    setOpenSlotDialog(true);
    await fetchTutorSlotPopupData();
  };

  const handleCloseSlotDialog = () => {
    if (slotDialogSaving) return;
    setOpenSlotDialog(false);
  };

  const handleUpdateActiveSlot = async () => {
    const busySet = new Set((Array.isArray(busySlotIds) ? busySlotIds : []).map(String));

    try {
      if (!classData?._id) throw new Error("Missing class session id");
      if (!selectedSlotId) {
        toast.error("Please select a slot");
        return;
      }
      if (busySet.has(String(selectedSlotId))) {
        toast.error("Selected slot is busy for this tutor");
        return;
      }

      setSlotDialogSaving(true);
      const res = await axios.put(`/classes/session/${classData._id}`, {
        finalisedActiveSlot: selectedSlotId,
      });

      if (res?.data?.success) {
        toast.success("Class slot updated successfully");
        setOpenSlotDialog(false);
        if (typeof refreshClassData === "function") {
          await refreshClassData();
        }
      } else {
        throw new Error(res?.data?.message || "Failed to update slot");
      }
    } catch (error) {
      console.error("Slot update error:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Failed to update slot"
      );
    } finally {
      setSlotDialogSaving(false);
    }
  };

  // Calculate dynamic pricing and session information
  // IMPORTANT: Use totalNoOfClasses first (total for all cycles), then fallback to first cycle
  const totalClasses = getTotalClassesForSession();
  const totalMonths = classData?.noOfMonths || 1;
  const paymentAmount = classData?.paymentAmount || 0;
  const selectedCycle = getSelectedCycle();
  const selectedCycleSessions = getSelectedCycleSessions(selectedCycle) || totalClasses;
  const selectedCycleMonthlyFee = getCycleMonthlyFee(selectedCycle);
  const selectedCycleTutorPay = getCycleTutorPay(selectedCycle);
  const monthlyClasses = selectedCycleSessions;
  const weeklyClasses = monthlyClasses / 4;

  // console.log("📊 Classes Calculation:");
  // console.log("Total Classes (all cycles):", classData?.totalNoOfClasses);
  // console.log("First Cycle Classes:", classData?.cycles?.[0]?.totalNoOfClasses);
  // console.log("Using Total Classes:", totalClasses);

  // Duration can be in minutes or hours - need to normalize to hours
  const durationValue = classData?.duration || 60; // default 60 minutes
  const sessionDurationInHours = durationValue > 24 ? durationValue / 60 : durationValue; // if > 24, it's in minutes, convert to hours

  const weeklyHours = weeklyClasses * sessionDurationInHours;
  const monthlyHours = weeklyHours * 4;
  const totalHours = totalClasses * sessionDurationInHours;

  // console.log("⏱️ Duration Value:", durationValue);
  // console.log("⏱️ Session Duration (hours):", sessionDurationInHours);

  // Class-level rates are updated when a tutor is replaced. Prefer them so
  // package details reflect the current tutor instead of the original cycle.
  const currentFeeCharge = toSafeNumber(classData?.perSessionFee);
  const currentTutorSalary = toSafeNumber(classData?.tutorSalary);
  const cycleFeeCharge =
    selectedCycleMonthlyFee > 0
      ? selectedCycleMonthlyFee / Math.max(selectedCycleSessions, 1)
      : getPerClassFeeCharge();
  const cycleTutorSalary =
    selectedCycleTutorPay > 0
      ? selectedCycleTutorPay / Math.max(selectedCycleSessions, 1)
      : 0;
  const feeCharge = currentFeeCharge > 0 ? currentFeeCharge : cycleFeeCharge;
  const tutorSalary = currentTutorSalary > 0 ? currentTutorSalary : cycleTutorSalary;

  const parentName = parent?.fullName || parent?.name || "N/A";
  const studentNames =
    studentsList?.length > 0
      ? studentsList
          .map((item) => item?.name)
          .filter(Boolean)
          .join(", ")
      : student?.name || "N/A";

  const remainingSessionsForReplacement = getRemainingClassesCount();
  const parentRemainingAmount = getRemainingAmountForReplacement();
  const replacementNoOfSessionNum = Math.floor(toSafeNumber(replacementNoOfSession));
  const replacementHourlyRateNum = toSafeNumber(replacementHourlyRate);
  const replacementMarginNum = toSafeNumber(replacementMargin);
  const newTutorPayoutAmount = replacementHourlyRateNum * replacementNoOfSessionNum;
  const replacementFeeCharge = replacementHourlyRateNum * (1 + replacementMarginNum / 100);
  const payoutExceeded =
    replacementNoOfSessionNum > 0 &&
    replacementHourlyRateNum > 0 &&
    newTutorPayoutAmount > parentRemainingAmount;
  // const approvedTutorCount = approvedTutors.length;
  const previousTutorName = tutor?.fullName || tutor?.name || "N/A";
  const previousTutorRate = toSafeNumber(classData?.tutorSalary || tutor?.hourlyRate || 0);
  const previousFeeCharge = toSafeNumber(classData?.feeCharge || feeCharge || 0);
  const previousTutorNoOfSessions = toSafeNumber(classData?.completedClasses || 0);
  const selectedReplacementTutor = approvedTutors.find(
    (item) => String(item.id) === String(selectedReplacementTutorId)
  );
  const selectedReplacementTutorName = selectedReplacementTutor?.fullName || "N/A";
  const selectedTutorNoOfSessions = replacementNoOfSessionNum;

  const weeklySessionCount = classData?.weeklySessionCount || Math.round(weeklyClasses);
  const monthlySessionCount =
    classData?.monthlySessionCount || Math.round(monthlyClasses);

  // Current rates must drive the summary after a tutor replacement.
  const cycleMonthlyFee =
    selectedCycleMonthlyFee > 0 ? selectedCycleMonthlyFee : feeCharge * monthlySessionCount;
  const cycleMonthlyTutorSalary =
    selectedCycleTutorPay > 0 ? selectedCycleTutorPay : tutorSalary * monthlySessionCount;
  const monthlyFee =
    currentFeeCharge > 0 ? feeCharge * monthlySessionCount : cycleMonthlyFee;
  const monthlyTutorSalary =
    currentTutorSalary > 0
      ? tutorSalary * monthlySessionCount
      : cycleMonthlyTutorSalary;
  const profitAmount = monthlyFee - monthlyTutorSalary;
  const monthProfitPercent = monthlyFee ? Math.round((profitAmount / monthlyFee) * 100) : 0;
  const profitColor = getProfitColor(monthProfitPercent);
  const profitPercentLabel =
    monthProfitPercent > 0 ? `${monthProfitPercent}%` : `${monthProfitPercent}%`;

  const totalPackageAmount = getTotalCycleValue("monthlyFee", paymentAmount);
  const totalPackageTutorSalary = getTotalCycleValue("tutorPay", classData?.tutorSalary || 0);
  const totalPackageProfit = totalPackageAmount - totalPackageTutorSalary;
  const totalPackageProfitPercentage =
    totalPackageAmount > 0 ? Math.round((totalPackageProfit / totalPackageAmount) * 100) : 0;

  console.log("💰 Using Stored Values from Backend:");
  console.log("Fee Charge per class (stored):", feeCharge);
  console.log("Tutor Salary per class (stored):", tutorSalary);
  console.log("Monthly Sessions:", monthlySessionCount);
  console.log("Monthly Fee (calculated):", monthlyFee);
  console.log("Monthly Tutor Salary (calculated):", monthlyTutorSalary);
  console.log("Payment Amount:", classData?.paymentAmount);
  console.log("Total Classes:", totalClasses);

  return (
    <ArgonBox>
      <Card
        sx={{
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 6px 4px rgba(0,0,0,0.12)",
          mb: 3,
          background: "white",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card
              sx={{
                borderRadius: "16px",
                p: 2.5,
                boxShadow: "0 4px 15px rgba(0,0,0,0.06)",
                background: "linear-gradient(135deg, #ffffff 0%, #fbfbfb 100%)",
              }}
            >
              {/* --- Parent Info Section --- */}
              <Grid container spacing={2} alignItems="center" mb={2}>
                {/* Avatar + Name + Status */}
                <Grid item xs={12} sm={4} textAlign={{ xs: "center", sm: "left" }}>
                  <Avatar
                    src={parent.profileImage}
                    alt="Parent"
                    sx={{
                      width: 90,
                      height: 90,
                      border: "3px solid white",
                      boxShadow: "0 3px 10px rgba(0,0,0,0.15)",
                      mb: 1,
                    }}
                  />
                  <ArgonTypography variant="subtitle2" fontWeight="medium">
                    {parent.fullName || "N/A"}
                  </ArgonTypography>
                </Grid>

                {/* Parent Info Cards */}
                <Grid item xs={12} sm={8}>
                  <Grid container spacing={1.5}>
                    {/* Username */}
                    <Grid item xs={6} sm={4}>
                      <ArgonBox
                        sx={{
                          p: 1.5,
                          bgcolor: "#f9f9ff",
                          borderRadius: 2,
                          textAlign: "center",
                          "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
                        }}
                      >
                        <PersonIcon color="primary" sx={{ fontSize: 20, mb: 0.3 }} />
                        <ArgonTypography variant="caption" display="block" color="text">
                          Username
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="medium">
                          {parent.fullName || "N/A"}
                        </ArgonTypography>
                      </ArgonBox>
                    </Grid>

                    {/* Joining Date */}
                    <Grid item xs={6} sm={4}>
                      <ArgonBox
                        sx={{
                          p: 1.5,
                          bgcolor: "#f9f9ff",
                          borderRadius: 2,
                          textAlign: "center",
                          "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
                        }}
                      >
                        <CalendarTodayIcon color="primary" sx={{ fontSize: 20, mb: 0.3 }} />
                        <ArgonTypography variant="caption" display="block" color="text">
                          Joined
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="medium">
                          {parent.createdAt
                            ? new Date(parent.createdAt).toLocaleDateString()
                            : "N/A"}
                        </ArgonTypography>
                      </ArgonBox>
                    </Grid>

                    {/* Badge / Level */}
                    <Grid item xs={12} sm={4}>
                      <ArgonBox
                        sx={{
                          p: 1.5,
                          bgcolor: "#f9f9ff",
                          borderRadius: 2,
                          textAlign: "center",
                          "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
                        }}
                      >
                        <ArgonBox sx={{ fontSize: 20, mb: 0.3, color: "#fbc02d" }}>🎓</ArgonBox>
                        <ArgonTypography variant="caption" display="block" color="text">
                          Tutor Level
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="medium">
                          {tutor.tutorLevel || "Level 1"}
                        </ArgonTypography>
                      </ArgonBox>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              {/* Contact Information */}
              <Grid container spacing={2}>
                {/* Email */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      minHeight: 120,
                      background: "linear-gradient(135deg, #e3f2fd, #bbdefb)",
                      border: "1px solid #90caf9",
                    }}
                  >
                    <ArgonBox display="flex" alignItems="center" gap={1} mb={1}>
                      <EmailIcon sx={{ color: "#1976d2", fontSize: 20 }} />
                      <ArgonTypography variant="subtitle2" fontWeight="medium">
                        Email ID
                      </ArgonTypography>
                    </ArgonBox>
                    <ArgonTypography variant="body2" fontWeight="medium">
                      {parent.email || "N/A"}
                    </ArgonTypography>
                  </Card>
                </Grid>

                {/* Tutor */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      minHeight: 120,
                      background: "linear-gradient(135deg, #e8f5e8, #c8e6c9)",
                      border: "1px solid #81c784",
                    }}
                  >
                    <ArgonBox display="flex" alignItems="center" gap={1} mb={1}>
                      <PersonIcon sx={{ color: "#388e3c", fontSize: 20 }} />
                      <ArgonTypography variant="subtitle2" fontWeight="medium">
                        Tutor
                      </ArgonTypography>
                    </ArgonBox>
                    <ArgonTypography variant="body2" fontWeight="medium">
                      {tutor.fullName || "N/A"}
                    </ArgonTypography>
                    <ArgonButton
                      variant="outlined"
                      color="success"
                      size="small"
                      sx={{ mt: 1.25 }}
                      onClick={handleOpenTutorReplacementDialog}
                    >
                      Tutor Replacement
                    </ArgonButton>
                  </Card>
                </Grid>

                {/* Phone Number */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      minHeight: 120,
                      background: "linear-gradient(135deg, #fff3e0, #ffe0b2)",
                      border: "1px solid #ffb74d",
                    }}
                  >
                    <ArgonBox display="flex" alignItems="center" gap={1} mb={1}>
                      <PhoneIcon sx={{ color: "#f57c00", fontSize: 20 }} />
                      <ArgonTypography variant="subtitle2" fontWeight="medium">
                        Phone
                      </ArgonTypography>
                    </ArgonBox>
                    <ArgonTypography variant="body2" fontWeight="medium">
                      {parent.phone || "N/A"}
                    </ArgonTypography>
                  </Card>
                </Grid>

                {/* Address */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      minHeight: 120,
                      background: "linear-gradient(135deg, #e0feffff, #b2e5ffff)",
                      border: "1px solid #4dffedff",
                    }}
                  >
                    <ArgonBox display="flex" alignItems="center" gap={1} mb={1}>
                      <LocationOnIcon sx={{ color: "#00acc1", fontSize: 20 }} />
                      <ArgonTypography variant="subtitle2" fontWeight="medium">
                        Address
                      </ArgonTypography>
                    </ArgonBox>
                    <ArgonTypography variant="caption" color="text" fontWeight="medium">
                      {`${student?.parentId?.address || ""} ${
                        student?.parentId?.city || ""
                      }`.trim() || "N/A"}
                    </ArgonTypography>
                  </Card>
                </Grid>

                {/* Mode */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      minHeight: 120,
                      background: "linear-gradient(135deg, #fce4ec, #f8bbd0)",
                      border: "1px solid #f06292",
                    }}
                  >
                    <ArgonBox display="flex" alignItems="center" gap={1} mb={1}>
                      <School sx={{ color: "#c2185b", fontSize: 20 }} />
                      <ArgonTypography variant="subtitle2" fontWeight="medium">
                        Mode
                      </ArgonTypography>
                    </ArgonBox>
                    <ArgonTypography variant="body2" fontWeight="medium">
                      {classData?.mode
                        ? classData.mode.charAt(0).toUpperCase() +
                          classData.mode.slice(1).toLowerCase()
                        : "N/A"}
                    </ArgonTypography>
                  </Card>
                </Grid>

                {/* Class Status */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      minHeight: 120,
                      background: "linear-gradient(135deg, #e3f2fd, #bbdefb)",
                      border: "1px solid #90caf9",
                    }}
                  >
                    <ArgonBox
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={1}
                    >
                      <ArgonBox display="flex" alignItems="center" gap={1}>
                        <CalendarToday sx={{ color: "#1976d2", fontSize: 20 }} />
                        <ArgonTypography variant="subtitle2" fontWeight="medium">
                          Class Status
                        </ArgonTypography>
                      </ArgonBox>
                      <IconButton
                        size="small"
                        onClick={handleOpenStatusDialog}
                        sx={{ color: "#1976d2" }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </ArgonBox>

                    {classStatus ? (
                      <Chip
                        size="small"
                        color={getClassStatusColor(classStatus)}
                        label={formatClassSessionStatusLabel(classStatus)}
                        sx={{ fontWeight: 600 }}
                      />
                    ) : (
                      <ArgonTypography variant="body2" fontWeight="medium">
                        N/A
                      </ArgonTypography>
                    )}
                  </Card>
                </Grid>

                {/* Active Slot (Finalised) */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      minHeight: 120,
                      background: "linear-gradient(135deg, #e8f5e8, #c8e6c9)",
                      border: "1px solid #81c784",
                    }}
                  >
                    <ArgonBox
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={1}
                    >
                      <ArgonBox display="flex" alignItems="center" gap={1}>
                        <Schedule sx={{ color: "#388e3c", fontSize: 20 }} />
                        <ArgonTypography variant="subtitle2" fontWeight="medium">
                          Active Slot
                        </ArgonTypography>
                      </ArgonBox>
                      <IconButton
                        size="small"
                        onClick={handleOpenSlotDialog}
                        sx={{ color: "#388e3c" }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </ArgonBox>

                    <ArgonTypography variant="body2" fontWeight="medium">
                      {currentFinalSlotLabel}
                    </ArgonTypography>
                  </Card>
                </Grid>

                {/* Meeting Link */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      minHeight: 120,
                      background: "linear-gradient(135deg, #f3e5f5, #e1bee7)",
                      border: "1px solid #ba68c8",
                    }}
                  >
                    <ArgonBox
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={1}
                    >
                      <ArgonBox display="flex" alignItems="center" gap={1}>
                        <VideoCall sx={{ color: "#7b1fa2", fontSize: 20 }} />
                        <ArgonTypography variant="subtitle2" fontWeight="medium">
                          Meeting Link
                        </ArgonTypography>
                      </ArgonBox>
                      <IconButton
                        size="small"
                        onClick={handleOpenMeetingDialog}
                        sx={{ color: "#7b1fa2" }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </ArgonBox>
                    {meetingLink ? (
                      <ArgonBox display="flex" alignItems="center" gap={1}>
                        <ArgonTypography
                          variant="caption"
                          color="text"
                          fontWeight="medium"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flex: 1,
                          }}
                        >
                          {meetingLink}
                        </ArgonTypography>
                        <IconButton
                          size="small"
                          onClick={handleCopyMeetingLink}
                          sx={{ color: "#7b1fa2" }}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </ArgonBox>
                    ) : (
                      <ArgonTypography variant="caption" color="text" fontWeight="medium">
                        No meeting link available
                      </ArgonTypography>
                    )}
                  </Card>
                </Grid>

                {/* Class Renew */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      minHeight: 120,
                      background: "linear-gradient(135deg, #e0f7fa, #b2ebf2)",
                      border: "1px solid #4dd0e1",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ArgonBox display="flex" flexDirection="column" alignItems="center" gap={1}>
                      <AutorenewIcon sx={{ color: "#00acc1", fontSize: 28 }} />
                      <ArgonButton
                        color="info"
                        variant="gradient"
                        size="medium"
                        onClick={handleOpenRenewDialog}
                        sx={{ px: 4, py: 1 }}
                      >
                        Class Renew
                      </ArgonButton>
                    </ArgonBox>
                  </Card>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </Card>

      <Card
        sx={{
          mt: 3,
          borderRadius: "16px",
          backgroundColor: "white",
          boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
          border: "1px solid #f0f0f0",
        }}
      >
        <ArgonBox p={3}>
          {/* Header */}
          <ArgonBox display="flex" alignItems="center" gap={2} mb={3}>
            <ArgonBox
              sx={{
                backgroundColor: "white",
                borderRadius: "12px",
                p: 1.2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 3px 8px rgba(0,0,0,0.08)",
              }}
            >
              <Payment sx={{ color: "#344767", fontSize: "22px" }} />
            </ArgonBox>
            <ArgonBox>
              <ArgonTypography variant="h5" fontWeight="bold" color="dark">
                Package Details {classData?.currentCycle ? `- ${classData.currentCycle}` : ""}
              </ArgonTypography>
              <ArgonTypography variant="body2" color="text">
                Pricing and session information
                {classData?.currentCycle ? ` for ${classData.currentCycle} months` : ""}
              </ArgonTypography>
            </ArgonBox>
          </ArgonBox>

          <Grid container spacing={3}>
            {/* Fee & Salary */}
            <Grid item xs={12} md={4}>
              <ArgonBox
                sx={{
                  backgroundColor: "white",
                  borderRadius: "12px",
                  p: 2.5,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                  border: "1px solid #f5f5f5",
                  height: "100%",
                }}
              >
                <ArgonBox display="flex" alignItems="center" gap={1.5} mb={2}>
                  <AttachMoney sx={{ color: "#4caf50", fontSize: "20px" }} />
                  <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                    Class Rates
                  </ArgonTypography>
                </ArgonBox>

                <ArgonBox mb={2}>
                  <ArgonBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <ArgonTypography variant="body2" color="text" fontWeight="medium">
                      Fee Charge per class
                    </ArgonTypography>
                    <ArgonTypography variant="body2" fontWeight="medium" color="dark">
                      ₹ {Math.round(feeCharge)}
                    </ArgonTypography>
                  </ArgonBox>
                  <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                    <ArgonTypography variant="body2" color="text" fontWeight="medium">
                      Tutor Salary per class
                    </ArgonTypography>
                    <ArgonTypography variant="body2" fontWeight="medium" color="dark">
                      ₹ {Math.round(tutorSalary)}
                    </ArgonTypography>
                  </ArgonBox>
                </ArgonBox>
              </ArgonBox>
            </Grid>

            {/* Sessions */}
            <Grid item xs={12} md={4}>
              <ArgonBox
                sx={{
                  backgroundColor: "white",
                  borderRadius: "12px",
                  p: 2.5,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                  border: "1px solid #f5f5f5",
                  height: "100%",
                }}
              >
                <ArgonBox display="flex" alignItems="center" gap={1.5} mb={2}>
                  <Schedule sx={{ color: "#2196f3", fontSize: "20px" }} />
                  <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                    Sessions
                  </ArgonTypography>
                </ArgonBox>

                <ArgonBox mb={2}>
                  <ArgonBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <ArgonTypography variant="body2" color="text" fontWeight="medium">
                      Sessions in a week
                    </ArgonTypography>
                    <ArgonTypography variant="caption" fontWeight="medium" color="dark">
                      {weeklySessionCount} sessions | {weeklyHours.toFixed(1)} hours
                    </ArgonTypography>
                  </ArgonBox>
                  <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                    <ArgonTypography variant="body2" color="text" fontWeight="medium">
                      Sessions in a month
                    </ArgonTypography>
                    <ArgonTypography variant="caption" fontWeight="medium" color="dark">
                      {monthlySessionCount} sessions / {monthlyHours.toFixed(1)} hours
                    </ArgonTypography>
                  </ArgonBox>
                </ArgonBox>
              </ArgonBox>
            </Grid>

            {/* Monthly Summary */}
            <Grid item xs={12} md={4}>
              <ArgonBox
                sx={{
                  backgroundColor: "white",
                  borderRadius: "12px",
                  p: 2.5,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                  border: "1px solid #f5f5f5",
                  height: "100%",
                }}
              >
                <ArgonBox display="flex" alignItems="center" gap={1.5} mb={2}>
                  <Payment sx={{ color: "#344767", fontSize: "20px" }} />
                  <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                    Monthly Summary
                  </ArgonTypography>
                </ArgonBox>

                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <ArgonBox textAlign="center">
                      <ArgonTypography variant="caption" color="text">
                        Monthly Fee
                      </ArgonTypography>
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                        ₹{Math.round(monthlyFee)}
                      </ArgonTypography>
                    </ArgonBox>
                  </Grid>
                  <Grid item xs={4}>
                    <ArgonBox textAlign="center">
                      <ArgonTypography variant="caption" color="text">
                        Payscale
                      </ArgonTypography>
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                        ₹{Math.round(monthlyTutorSalary)}
                      </ArgonTypography>
                    </ArgonBox>
                  </Grid>
                  <Grid item xs={4}>
                    <ArgonBox textAlign="center">
                      <ArgonTypography variant="caption" color="text">
                        Profit
                      </ArgonTypography>
                      <ArgonTypography
                        variant="h6"
                        fontWeight="bold"
                        color={profitColor}
                        sx={{
                          whiteSpace: "nowrap",
                          color:
                            monthProfitPercent >= 20 && monthProfitPercent <= 25
                              ? "#E6A700 !important"
                              : undefined,
                        }}
                      >
                        ₹{Math.round(profitAmount)} ({profitPercentLabel})
                      </ArgonTypography>
                    </ArgonBox>
                  </Grid>
                </Grid>
              </ArgonBox>
            </Grid>
          </Grid>
        </ArgonBox>
      </Card>

      <ArgonBox py={3}>
        <ArgonTypography variant="body1" fontWeight="medium" sx={{ px: 3 }} mb={2}>
          Student List
        </ArgonTypography>
        <ArgonBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": {
                borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                  `${borderWidth[1]} solid ${borderColor}`,
              },
            },
          }}
        >
          <Table columns={columns} rows={rows} />
        </ArgonBox>
      </ArgonBox>

      <ArgonBox display="flex" gap={3} mt={3}>
        {/* Core Requirements Card */}
        <ArgonBox
          sx={{
            width: "50%",
            bgcolor: "background.paper",
            borderRadius: "16px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
            transition: "all 0.3s ease",
            "&:hover": { boxShadow: "0 8px 20px rgba(0,0,0,0.18)" },
          }}
          p={3}
        >
          {/* Header Row */}
          <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <ArgonBox display="flex" alignItems="center" gap={1}>
              <ArgonTypography variant="h6" fontWeight="bold" color="primary">
                Core Requirements
              </ArgonTypography>
            </ArgonBox>

            <ArgonButton
              type="button"
              onClick={handleOpenCoreDialog}
              variant="contained"
              size="small"
              color="primary"
            >
              Add
            </ArgonButton>
          </ArgonBox>

          {coreLoading ? (
            <ArgonBox display="flex" justifyContent="center" py={2}>
              <CircularProgress size={22} />
            </ArgonBox>
          ) : coreRequirements.length === 0 ? (
            <ArgonTypography variant="body2" color="text">
              No core requirements added yet
            </ArgonTypography>
          ) : (
            <ArgonBox display="flex" flexDirection="column" gap={1}>
              {coreRequirements.slice(0, 3).map((item, index) => (
                <ArgonTypography key={item?._id || index} variant="body2" color="text">
                  {index + 1}. {item?.requirement || "NA"}
                </ArgonTypography>
              ))}
            </ArgonBox>
          )}

          <ArgonTypography
            variant="body2"
            color="secondary"
            sx={{
              cursor: "pointer",
              fontWeight: "bold",
              letterSpacing: 0.5,
              transition: "color 0.2s",
              mt: 2,
              "&:hover": { color: "#2379f1ff" },
            }}
            onClick={handleOpenShowAllCore}
          >
            Show All Requirements →
          </ArgonTypography>
        </ArgonBox>

        {/* Notes Card */}
        <ArgonBox
          sx={{
            width: "50%",
            bgcolor: "background.paper",
            borderRadius: "16px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
            transition: "all 0.3s ease",
            "&:hover": { boxShadow: "0 8px 20px rgba(0,0,0,0.18)" },
          }}
          p={3}
        >
          {/* Header Row */}
          <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <ArgonBox display="flex" alignItems="center" gap={1}>
              <span style={{ fontSize: 28, marginRight: 6 }}>📝</span>
              <ArgonTypography variant="h6" fontWeight="bold" color="primary">
                Notes
              </ArgonTypography>
            </ArgonBox>

            <ArgonButton
              type="button"
              onClick={handleOpenNotesDialog}
              variant="contained"
              size="medium"
              color="primary"
            >
              Add Note
            </ArgonButton>
          </ArgonBox>

          {notesLoading ? (
            <ArgonBox display="flex" justifyContent="center" py={2}>
              <CircularProgress size={22} />
            </ArgonBox>
          ) : classNotes.length === 0 ? (
            <ArgonTypography variant="body2" color="text">
              No notes added yet
            </ArgonTypography>
          ) : (
            <ArgonBox display="flex" flexDirection="column" gap={1}>
              {classNotes.slice(0, 3).map((item, index) => (
                <ArgonTypography key={item?._id || index} variant="body2" color="text">
                  {index + 1}. {item?.note || "NA"}
                </ArgonTypography>
              ))}
            </ArgonBox>
          )}

          <ArgonTypography
            variant="body2"
            color="secondary"
            sx={{
              cursor: "pointer",
              fontWeight: "bold",
              letterSpacing: 0.5,
              transition: "color 0.2s",
              mt: 2,
              "&:hover": { color: "#2379f1ff" },
            }}
            onClick={handleOpenShowAllNotes}
          >
            Show All Notes →
          </ArgonTypography>
        </ArgonBox>
      </ArgonBox>

      <Dialog
        open={coreDialogOpen}
        onClose={handleCloseCoreDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>Add Core Requirement</DialogTitle>
        <DialogContent>
          <ArgonBox mt={2}>
            <ArgonInput
              multiline
              rows={4}
              fullWidth
              placeholder="Enter core requirement"
              value={coreInput}
              onChange={(e) => setCoreInput(e.target.value)}
            />
          </ArgonBox>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <ArgonButton variant="outlined" color="secondary" onClick={handleCloseCoreDialog}>
            Cancel
          </ArgonButton>
          <ArgonButton
            type="button"
            variant="contained"
            color="primary"
            onClick={handleSaveCoreRequirement}
            disabled={savingCore}
          >
            {savingCore ? "Saving..." : "Save"}
          </ArgonButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={notesDialogOpen}
        onClose={handleCloseNotesDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>Add Note</DialogTitle>
        <DialogContent>
          <ArgonBox mt={2}>
            <ArgonInput
              multiline
              rows={4}
              fullWidth
              placeholder="Enter note"
              value={noteInput}
              onChange={(e) => setNoteInput(e.target.value)}
            />
          </ArgonBox>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <ArgonButton variant="outlined" color="secondary" onClick={handleCloseNotesDialog}>
            Cancel
          </ArgonButton>
          <ArgonButton
            type="button"
            variant="contained"
            color="primary"
            onClick={handleSaveClassNote}
            disabled={savingNote}
          >
            {savingNote ? "Saving..." : "Save"}
          </ArgonButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showAllCoreOpen}
        onClose={handleCloseShowAllCore}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>All Core Requirements</DialogTitle>
        <DialogContent>
          <ArgonBox py={2}>
            {coreLoading ? (
              <ArgonBox display="flex" justifyContent="center" py={4}>
                <CircularProgress size={24} />
              </ArgonBox>
            ) : coreRequirements.length === 0 ? (
              <ArgonBox display="flex" flexDirection="column" alignItems="center" py={4}>
                <ArgonTypography variant="body2" color="text" mt={1}>
                  No core requirements available
                </ArgonTypography>
              </ArgonBox>
            ) : (
              <ArgonBox
                sx={{
                  "& .MuiTableRow-root:not(:last-child)": {
                    "& td": {
                      borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                        `${borderWidth[1]} solid ${borderColor}`,
                    },
                  },
                }}
              >
                <Table
                  columns={[
                    { name: "S.No.", align: "center" },
                    { name: "Date & Time", align: "center" },
                    { name: "Requirement", align: "center" },
                  ]}
                  rows={coreRequirements.map((entry, index) => ({
                    "S.No.": (
                      <ArgonTypography variant="caption" color="text" fontWeight="medium">
                        {index + 1}
                      </ArgonTypography>
                    ),
                    "Date & Time": (
                      <ArgonTypography variant="caption" color="text" fontWeight="medium">
                        {formatDateTime(entry?.createdAt)}
                      </ArgonTypography>
                    ),
                    Requirement: (
                      <ArgonTypography variant="caption" color="text" fontWeight="medium">
                        {entry?.requirement || "NA"}
                      </ArgonTypography>
                    ),
                  }))}
                />
              </ArgonBox>
            )}
          </ArgonBox>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <ArgonButton variant="outlined" color="secondary" onClick={handleCloseShowAllCore}>
            Close
          </ArgonButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showAllNotesOpen}
        onClose={handleCloseShowAllNotes}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>All Notes</DialogTitle>
        <DialogContent>
          <ArgonBox py={2}>
            {notesLoading ? (
              <ArgonBox display="flex" justifyContent="center" py={4}>
                <CircularProgress size={24} />
              </ArgonBox>
            ) : classNotes.length === 0 ? (
              <ArgonBox display="flex" flexDirection="column" alignItems="center" py={4}>
                <ArgonTypography variant="body2" color="text" mt={1}>
                  No notes available
                </ArgonTypography>
              </ArgonBox>
            ) : (
              <ArgonBox
                sx={{
                  "& .MuiTableRow-root:not(:last-child)": {
                    "& td": {
                      borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                        `${borderWidth[1]} solid ${borderColor}`,
                    },
                  },
                }}
              >
                <Table
                  columns={[
                    { name: "S.No.", align: "center" },
                    { name: "Date", align: "center" },
                    { name: "Notes", align: "center" },
                  ]}
                  rows={classNotes.map((entry, index) => ({
                    "S.No.": (
                      <ArgonTypography variant="caption" color="text" fontWeight="medium">
                        {index + 1}
                      </ArgonTypography>
                    ),
                    Date: (
                      <ArgonTypography variant="caption" color="text" fontWeight="medium">
                        {formatDateTime(entry?.createdAt)}
                      </ArgonTypography>
                    ),
                    Notes: (
                      <ArgonTypography variant="caption" color="text" fontWeight="medium">
                        {entry?.note || "NA"}
                      </ArgonTypography>
                    ),
                  }))}
                />
              </ArgonBox>
            )}
          </ArgonBox>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <ArgonButton variant="outlined" color="secondary" onClick={handleCloseShowAllNotes}>
            Close
          </ArgonButton>
        </DialogActions>
      </Dialog>

      {/* Tutor Replacement Dialog */}
      <Dialog
        open={openTutorReplacementDialog}
        onClose={handleCloseTutorReplacementDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <ArgonBox display="flex" alignItems="center" gap={1}>
            <SwapHoriz sx={{ color: "#2e7d32" }} />
            <ArgonTypography variant="h6" fontWeight="bold">
              Tutor Replacement
            </ArgonTypography>
          </ArgonBox>
        </DialogTitle>

        <DialogContent>
          <ArgonBox pt={1}>
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <ArgonTypography
                  variant="button"
                  fontWeight="regular"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Parent Name
                </ArgonTypography>
                <ArgonInput label="Parent Name" value={parentName} disabled fullWidth />
              </Grid>

              <Grid item xs={12}>
                <ArgonTypography
                  variant="button"
                  fontWeight="regular"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Student Name
                </ArgonTypography>
                <ArgonInput label="Student Name" value={studentNames} disabled fullWidth />
              </Grid>

              <Grid item xs={12}>
                <ArgonTypography
                  variant="button"
                  fontWeight="regular"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Tutor Name
                </ArgonTypography>
                <Dropdown
                  value={selectedReplacementTutorId || ""}
                  onChange={(e) => handleTutorSelectionChange(e.target.value)}
                  width="100%"
                  size="medium"
                  options={[
                    {
                      label: approvedTutorsLoading ? "Loading approved tutors..." : "Select tutor",
                      value: "",
                      disabled: true,
                    },
                    ...sortedApprovedTutors.map((item) => {
                      const rateForLabel = resolveTutorHourlyRate(item);

                      const baseLabel = formatTutorLabel(item, item.distanceKm);

                      return {
                        label: `${baseLabel} (₹${Math.round(rateForLabel)}/hr)${
                          item.isCurrentTutor ? " - Current Tutor" : ""
                        }`,
                        value: item.id,
                        disabled: item.isCurrentTutor,
                      };
                    }),
                  ]}
                />
              </Grid>

              {selectedReplacementTutorId ? (
                <>
                  <Grid item xs={12} sm={4}>
                    <ArgonTypography
                      variant="button"
                      fontWeight="regular"
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      Hourly Rate (Tutor)
                    </ArgonTypography>
                    <ArgonInput
                      type="number"
                      fullWidth
                      value={replacementHourlyRate}
                      onChange={(e) => setReplacementHourlyRate(e.target.value)}
                      placeholder="Enter hourly rate"
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <ArgonTypography
                      variant="button"
                      fontWeight="regular"
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      No Of Session
                    </ArgonTypography>
                    <ArgonInput
                      type="number"
                      fullWidth
                      value={replacementNoOfSession}
                      onChange={(e) => setReplacementNoOfSession(e.target.value)}
                      placeholder="Enter no of session"
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <ArgonTypography
                      variant="button"
                      fontWeight="regular"
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      Margin (%)
                    </ArgonTypography>
                    <ArgonInput
                      type="number"
                      fullWidth
                      value={replacementMargin}
                      onChange={(e) => setReplacementMargin(e.target.value)}
                      placeholder="Enter margin %"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <ArgonBox
                      sx={{
                        borderRadius: 2,
                        px: 1.5,
                        py: 1.5,
                        backgroundColor: "#f8fafb",
                        border: payoutExceeded ? "1px solid #ef9a9a" : "1px solid #d9e2ec",
                      }}
                    >
                      <Grid container spacing={1.25}>
                        <Grid item xs={12} md={4}>
                          <ArgonBox
                            sx={{
                              p: 1,
                              borderRadius: 1.5,
                              backgroundColor: "#eef4ff",
                              border: "1px solid #c7d7fe",
                              height: "100%",
                            }}
                          >
                            <ArgonTypography variant="caption" fontWeight="bold" color="info">
                              Parent Summary
                            </ArgonTypography>
                            <ArgonTypography
                              variant="caption"
                              color="text"
                              display="block"
                              mt={0.5}
                            >
                              Remaining Sessions: {remainingSessionsForReplacement}
                            </ArgonTypography>
                            <ArgonTypography variant="caption" color="text" display="block">
                              Remaining Amount: ₹{Math.round(parentRemainingAmount)}
                            </ArgonTypography>
                          </ArgonBox>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <ArgonBox
                            sx={{
                              p: 1,
                              borderRadius: 1.5,
                              backgroundColor: "#fff8e8",
                              border: "1px solid #ffe2a8",
                              height: "100%",
                            }}
                          >
                            <ArgonTypography
                              variant="caption"
                              fontWeight="bold"
                              sx={{ color: "#8a5700" }}
                            >
                              Previous Tutor
                            </ArgonTypography>
                            <ArgonTypography
                              variant="caption"
                              color="text"
                              display="block"
                              mt={0.5}
                            >
                              Name: {previousTutorName}
                            </ArgonTypography>
                            <ArgonTypography variant="caption" color="text" display="block">
                              Rate/Class: ₹{Math.round(previousTutorRate)}
                            </ArgonTypography>
                            <ArgonTypography variant="caption" color="text" display="block">
                              Completed Sessions: {previousTutorNoOfSessions}
                            </ArgonTypography>
                            <ArgonTypography variant="caption" color="text" display="block">
                              Remaining Sessions: {remainingSessionsForReplacement}
                            </ArgonTypography>
                            <ArgonTypography variant="caption" color="text" display="block">
                              Fee Charge/Class: ₹{Math.round(previousFeeCharge)}
                            </ArgonTypography>
                          </ArgonBox>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <ArgonBox
                            sx={{
                              p: 1,
                              borderRadius: 1.5,
                              backgroundColor: payoutExceeded ? "#ffebee" : "#e8f5e9",
                              border: payoutExceeded ? "1px solid #ef9a9a" : "1px solid #a5d6a7",
                              height: "100%",
                            }}
                          >
                            <ArgonTypography
                              variant="caption"
                              fontWeight="bold"
                              color={payoutExceeded ? "error" : "success"}
                            >
                              New Tutor Estimate
                            </ArgonTypography>
                            <ArgonTypography
                              variant="caption"
                              color="text"
                              display="block"
                              mt={0.5}
                            >
                              Name: {selectedReplacementTutorName}
                            </ArgonTypography>
                            <ArgonTypography variant="caption" color="text" display="block">
                              Rate/Class: ₹{Math.round(replacementHourlyRateNum)}
                            </ArgonTypography>
                            <ArgonTypography variant="caption" color="text" display="block">
                              NoOfSessions: {selectedTutorNoOfSessions}
                            </ArgonTypography>
                            <ArgonTypography variant="caption" color="text" display="block">
                              Fee Charge/Class: ₹{Math.round(replacementFeeCharge)}
                            </ArgonTypography>
                          </ArgonBox>
                        </Grid>
                      </Grid>
                    </ArgonBox>
                  </Grid>
                </>
              ) : null}
            </Grid>
          </ArgonBox>
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 0 }}>
          <ArgonButton
            onClick={handleCloseTutorReplacementDialog}
            variant="outlined"
            color="secondary"
            size="small"
            disabled={replacementSaving}
          >
            Cancel
          </ArgonButton>
          <ArgonButton
            onClick={handleSubmitTutorReplacement}
            color="primary"
            size="small"
            variant="contained"
            disabled={replacementSaving || !selectedReplacementTutorId || payoutExceeded}
          >
            {replacementSaving ? "Updating..." : "Submit"}
          </ArgonButton>
        </DialogActions>
      </Dialog>

      {/* Meeting Link Update Dialog */}
      <Dialog open={openMeetingDialog} onClose={handleCloseMeetingDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <ArgonBox display="flex" alignItems="center" gap={1}>
            <VideoCall sx={{ color: "#7b1fa2" }} />
            <ArgonTypography variant="h6" fontWeight="bold">
              Update Meeting Link
            </ArgonTypography>
          </ArgonBox>
        </DialogTitle>
        <DialogContent>
          <ArgonBox pt={1}>
            <ArgonInput
              fullWidth
              label="Meeting Link"
              placeholder="Enter meeting link (e.g., https://meet.google.com/xxx-xxxx-xxx)"
              value={meetingLinkInput}
              onChange={(e) => setMeetingLinkInput(e.target.value)}
              variant="outlined"
              multiline
              rows={2}
              sx={{ mt: 1 }}
            />
          </ArgonBox>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <ArgonButton
            onClick={handleCloseMeetingDialog}
            variant="outlined"
            color="secondary"
            size="small"
          >
            Cancel
          </ArgonButton>
          <ArgonButton
            onClick={handleUpdateMeetingLink}
            color="primary"
            size="small"
            variant="contained"
          >
            Update
          </ArgonButton>
        </DialogActions>
      </Dialog>

      {/* Class Status Update Dialog */}
      <Dialog open={openStatusDialog} onClose={handleCloseStatusDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          <ArgonBox display="flex" alignItems="center" gap={1}>
            <CalendarToday sx={{ color: "#1976d2" }} />
            <ArgonTypography variant="h6" fontWeight="bold">
              Update Class Status
            </ArgonTypography>
          </ArgonBox>
        </DialogTitle>
        <DialogContent>
          <ArgonBox pt={1}>
            <ArgonTypography
              variant="button"
              fontWeight="regular"
              sx={{ display: "block", mb: 0.5 }}
            >
              Class Status
            </ArgonTypography>
            <Dropdown
              value={statusInput || ""}
              onChange={(e) => setStatusInput(e.target.value)}
              width="100%"
              size="medium"
              options={[
                { label: "Select status", value: "", disabled: true },
                ...CLASS_SESSION_STATUS_OPTIONS.map((s) => ({
                  label: formatClassSessionStatusLabel(s),
                  value: s,
                })),
              ]}
            />
          </ArgonBox>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <ArgonButton
            onClick={handleCloseStatusDialog}
            variant="outlined"
            color="secondary"
            size="small"
            disabled={statusSaving}
          >
            Cancel
          </ArgonButton>
          <ArgonButton
            onClick={handleUpdateClassStatus}
            color="primary"
            size="small"
            variant="contained"
            disabled={statusSaving || !statusInput}
          >
            {statusSaving ? "Updating..." : "Update"}
          </ArgonButton>
        </DialogActions>
      </Dialog>

      {/* Active Slot Update Dialog */}
      <Dialog open={openSlotDialog} onClose={handleCloseSlotDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <ArgonBox display="flex" alignItems="center" gap={1}>
            <Schedule color="primary" />
            <ArgonTypography variant="h6" fontWeight="bold">
              Update Class Slot
            </ArgonTypography>
          </ArgonBox>
        </DialogTitle>

        <DialogContent>
          <ArgonBox pt={1}>
            <ArgonTypography variant="body2" color="text" fontWeight="medium">
              Current: {currentFinalSlotLabel}
            </ArgonTypography>

            <ArgonBox display="flex" gap={1} flexWrap="wrap" mt={2}>
              <Chip size="small" color="primary" label="Current" />
              <Chip size="small" color="success" label="Available" />
              <Chip size="small" color="error" label="Busy" />
              <Chip size="small" color="secondary" label="Preferred" />
            </ArgonBox>

            {slotDialogLoading ? (
              <ArgonBox display="flex" justifyContent="center" alignItems="center" py={4}>
                <CircularProgress color="primary" size={28} />
              </ArgonBox>
            ) : allSlots.length > 0 ? (
              <ArgonBox display="flex" flexWrap="wrap" gap={1.25} mt={2}>
                {allSlots.map((slot) => {
                  const id = getSlotId(slot);
                  const busySet = new Set(
                    (Array.isArray(busySlotIds) ? busySlotIds : []).map(String)
                  );
                  const isCurrent =
                    id && currentFinalSlotId ? String(id) === String(currentFinalSlotId) : false;
                  const isBusy = !isCurrent && id ? busySet.has(String(id)) : false;
                  const status = isCurrent ? "current" : isBusy ? "busy" : "available";

                  const isPreferred = id ? preferredSlotIdSet.has(String(id)) : false;

                  const isSelected =
                    id && selectedSlotId ? String(id) === String(selectedSlotId) : false;

                  const clickable = status !== "busy";

                  const getPalette = (theme) => {
                    if (status === "current") {
                      return {
                        bg: alpha(theme.palette.primary.main, 0.12),
                        border: alpha(theme.palette.primary.main, 0.45),
                      };
                    }
                    if (status === "busy") {
                      return {
                        bg: alpha(theme.palette.error.main, 0.12),
                        border: alpha(theme.palette.error.main, 0.45),
                      };
                    }
                    return {
                      bg: alpha(theme.palette.success.main, 0.12),
                      border: alpha(theme.palette.success.main, 0.45),
                    };
                  };

                  return (
                    <ArgonBox
                      key={id || getSlotLabel(slot)}
                      onClick={clickable && id ? () => setSelectedSlotId(String(id)) : undefined}
                      sx={(theme) => {
                        const pal = getPalette(theme);
                        return {
                          px: 1.5,
                          py: 1,
                          borderRadius: 2,
                          minWidth: 120,
                          textAlign: "center",
                          border: "2px solid",
                          borderColor: isSelected ? theme.palette.primary.main : pal.border,
                          bgcolor: pal.bg,
                          cursor: clickable ? "pointer" : "not-allowed",
                          opacity: status === "busy" ? 0.8 : 1,
                        };
                      }}
                    >
                      <ArgonBox display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                        <ArgonTypography variant="caption" fontWeight="bold" color="dark">
                          {getSlotLabel(slot)}
                        </ArgonTypography>
                        {isPreferred ? (
                          <Chip
                            size="small"
                            label="Preferred"
                            color="secondary"
                            // variant="outlined"
                            sx={{
                              height: 18,
                              "& .MuiChip-label": { px: 0.75, fontSize: "0.65rem" },
                            }}
                          />
                        ) : null}
                      </ArgonBox>
                    </ArgonBox>
                  );
                })}
              </ArgonBox>
            ) : (
              <ArgonBox py={2}>
                <ArgonTypography variant="body2" color="text">
                  No slots found.
                </ArgonTypography>
              </ArgonBox>
            )}
          </ArgonBox>
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 0 }}>
          <ArgonButton
            onClick={handleCloseSlotDialog}
            variant="outlined"
            color="secondary"
            size="small"
            disabled={slotDialogSaving}
          >
            Cancel
          </ArgonButton>
          <ArgonButton
            onClick={handleUpdateActiveSlot}
            color="primary"
            size="small"
            variant="contained"
            disabled={slotDialogSaving || !selectedSlotId}
          >
            {slotDialogSaving ? "Updating..." : "Update"}
          </ArgonButton>
        </DialogActions>
      </Dialog>

      {/* Class Renew Dialog */}
      <Dialog open={openRenewDialog} onClose={handleCloseRenewDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <ArgonBox display="flex" alignItems="center" justifyContent="space-between">
            <ArgonBox display="flex" alignItems="center" gap={1}>
              <Payment sx={{ color: "#1976d2" }} />
              <ArgonTypography variant="h6" fontWeight="bold">
                Class Renew
              </ArgonTypography>
            </ArgonBox>
            <IconButton
              size="small"
              onClick={handleCloseRenewDialog}
              aria-label="close class renew"
            >
              <Close fontSize="small" />
            </IconButton>
          </ArgonBox>
        </DialogTitle>
        <DialogContent>
          <ArgonBox pt={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <ArgonTypography
                  variant="button"
                  fontWeight="regular"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Parent Name
                </ArgonTypography>
                <ArgonInput value={parentName} disabled fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ArgonTypography
                  variant="button"
                  fontWeight="regular"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Student Name
                </ArgonTypography>
                <ArgonInput value={studentNames} disabled fullWidth />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ArgonTypography
                  variant="button"
                  fontWeight="regular"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Tutor
                </ArgonTypography>
                <ArgonInput value={tutor?.fullName || tutor?.name || "N/A"} disabled fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ArgonTypography
                  variant="button"
                  fontWeight="regular"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Classes Per Week
                </ArgonTypography>
                <ArgonInput value={sessionsPerWeek || 0} disabled fullWidth />
              </Grid>

              <Grid item xs={12}>
                <ArgonTypography
                  variant="button"
                  fontWeight="regular"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Select Plan
                </ArgonTypography>
                <Dropdown
                  value={selectedPlanId || ""}
                  onChange={(e) => setSelectedPlanId(e.target.value)}
                  width="100%"
                  size="medium"
                  options={[
                    {
                      label: plansLoading ? "Loading plans..." : "Select plan",
                      value: "",
                      disabled: true,
                    },
                    ...purchasePlans.map((plan) => ({
                      label: `${plan.title || plan.duration || "Plan"} (${plan.months} Months)`,
                      value: plan._id,
                    })),
                  ]}
                />
                {selectedPlanId && (
                  <ArgonBox
                    sx={{
                      mt: 0.75,
                      fontWeight: "bold",
                      color: "#d32f2f",
                      backgroundColor: "#fff3f3",
                      px: 1,
                      py: 0.5,
                      borderRadius: "4px",
                    }}
                  >
                    <ArgonTypography variant="caption" sx={{ color: "inherit" }}>
                      (Note: Adjust per class as per previously charged fee to maintain margin)
                    </ArgonTypography>
                  </ArgonBox>
                )}
              </Grid>

              {/* <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid> */}

              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    background: "linear-gradient(135deg, #e3f2fd, #bbdefb)",
                    border: "1px solid #90caf9",
                  }}
                >
                  <ArgonTypography variant="subtitle2" fontWeight="medium" mb={1}>
                    Plan Summary
                  </ArgonTypography>
                  <ArgonBox mb={1}>
                    <ArgonTypography variant="caption" display="block">
                      Total Sessions
                    </ArgonTypography>
                    <ArgonInput
                      type="number"
                      value={renewValues.totalSessions}
                      onChange={handleRenewValueChange("totalSessions")}
                      fullWidth
                    />
                  </ArgonBox>
                  <ArgonBox mb={1}>
                    <ArgonTypography variant="caption" display="block">
                      Total Cycles
                    </ArgonTypography>
                    <ArgonInput
                      type="number"
                      value={renewValues.totalCycles}
                      onChange={handleRenewValueChange("totalCycles")}
                      fullWidth
                    />
                  </ArgonBox>
                  <ArgonBox>
                    <ArgonTypography variant="caption" display="block">
                      Sessions / Month
                    </ArgonTypography>
                    <ArgonInput
                      type="number"
                      value={renewValues.sessionsPerMonth}
                      onChange={handleRenewValueChange("sessionsPerMonth")}
                      fullWidth
                    />
                  </ArgonBox>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    background: "linear-gradient(135deg, #e8f5e9, #c8e6c9)",
                    border: "1px solid #81c784",
                  }}
                >
                  <ArgonTypography variant="subtitle2" fontWeight="medium" mb={1}>
                    Pricing Summary
                  </ArgonTypography>
                  <ArgonBox mb={1}>
                    <ArgonTypography variant="caption" display="block">
                      Tutor Payout / Class
                    </ArgonTypography>
                    <ArgonInput
                      type="number"
                      value={renewValues.tutorPayoutPerClass}
                      onChange={handleRenewValueChange("tutorPayoutPerClass")}
                      fullWidth
                    />
                  </ArgonBox>
                  <ArgonBox mb={1}>
                    <ArgonTypography variant="caption" display="block">
                      Per Class Fees
                    </ArgonTypography>
                    <ArgonInput
                      type="number"
                      value={renewValues.perClassFees}
                      onChange={handleRenewValueChange("perClassFees")}
                      fullWidth
                    />
                  </ArgonBox>
                  <ArgonBox mb={1}>
                    <ArgonTypography variant="caption" display="block">
                      Per Month Fees
                    </ArgonTypography>
                    <ArgonInput
                      type="number"
                      value={renewValues.perMonthFees}
                      onChange={handleRenewValueChange("perMonthFees")}
                      fullWidth
                    />
                  </ArgonBox>
                  <ArgonBox mb={1}>
                    <ArgonTypography variant="caption" display="block">
                      Tutor Payout (Total)
                    </ArgonTypography>
                    <ArgonInput
                      type="number"
                      value={renewValues.tutorPayoutTotal}
                      onChange={handleRenewValueChange("tutorPayoutTotal")}
                      fullWidth
                    />
                  </ArgonBox>
                  <ArgonBox>
                    <ArgonTypography variant="caption" display="block">
                      Total Amount
                    </ArgonTypography>
                    <ArgonInput
                      type="number"
                      value={renewValues.totalAmount}
                      onChange={handleRenewValueChange("totalAmount")}
                      fullWidth
                    />
                  </ArgonBox>
                </Card>
              </Grid>
            </Grid>
          </ArgonBox>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <ArgonButton
            onClick={handleCloseRenewDialog}
            variant="outlined"
            color="secondary"
            size="small"
            disabled={renewSaving}
          >
            Cancel
          </ArgonButton>
          <ArgonButton
            onClick={handleRenewClass}
            color="primary"
            size="small"
            variant="contained"
            disabled={renewSaving || !selectedPlanId}
          >
            {renewSaving ? "Renewing..." : "Renew Class"}
          </ArgonButton>
        </DialogActions>
      </Dialog>
    </ArgonBox>
  );
}

export default summary;
